import React, { useCallback, useEffect, useMemo, useState } from "react";
import AxiosInstance from "../../AxiosInstance";
import moment from "moment-timezone";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { handleAuth } from "../../../components/Login/Auth";
import "../../Customer/Appoiment/style.css";
import "./TodaysAppointments.css";
import { Box, Button as MuiButton, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import showToast from "../../../components/Toast/Toster.jsx";
import { getItem } from "../../../utils/storage";
import { categorizeVisitsByUserTimezone } from "../../../components/Dashboard/DashboardVisitLists";
import {
  getVisitAppointmentStatusTone,
  JobberAssignedWorkersCell,
  JobberAvatar,
  JobberListCard,
  JobberPagination,
  JobberSearch,
  JobberSorting,
  JobberStatusBadge,
  JobberTable,
  JobberTableDocRefTitleCell,
  jobberVisitAssignedWorkersLabel,
  jobberVisitWorkerProfiles,
} from "../../../components/MuiTable/index.jsx";
import { useUserType } from "../../../contexts/UserTypeContext.jsx";

/** Pastel View — used in mobile collapse Actions row (desktop column hidden via CSS for customer/worker) */
const COMPANY_VIEW_ACTION_BG = "#E0E7FF";
const COMPANY_VIEW_ICON_COLOR = "#1E3A8A";

const todaysMobileActionBtnBase = {
  width: "100%",
  minWidth: 0,
  alignSelf: "stretch",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.7rem",
  borderRadius: "8px",
  minHeight: 32,
  px: 0.5,
  py: 0.375,
  lineHeight: 1.15,
  boxShadow: "none",
  whiteSpace: "nowrap",
  justifyContent: "center",
  "& .MuiButton-startIcon": { mr: 0.25, ml: -0.1 },
};

function formatTimeForVisitDisplay(timeVal) {
  if (!timeVal) return null;
  const timeStr = String(timeVal).trim();
  if (!timeStr) return null;
  try {
    if (moment(timeVal, moment.ISO_8601, true).isValid() && /T/.test(timeStr)) {
      return moment(timeVal).format("hh:mm A");
    }
    if (timeStr.match(/[AaPp][Mm]/)) {
      const timeMoment = moment(
        timeStr,
        ["h:mm A", "hh:mm A", "H:mm A", "HH:mm A", "h:mm:ss A", "hh:mm:ss A"],
        true
      );
      return timeMoment.isValid() ? timeMoment.format("hh:mm A") : null;
    }
    const timeParts = timeStr.split(":");
    if (timeParts.length >= 2) {
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      if (
        !Number.isNaN(hours) &&
        !Number.isNaN(minutes) &&
        hours >= 0 &&
        hours < 24 &&
        minutes >= 0 &&
        minutes < 60
      ) {
        return moment().hour(hours).minute(minutes).second(0).format("hh:mm A");
      }
    }
    const parsed = moment(timeStr);
    return parsed.isValid() ? parsed.format("hh:mm A") : null;
  } catch {
    return null;
  }
}

function formatAppointmentDateDisplay(visit, formatDateWithUserTimezone, dateFormat) {
  if (!visit?.StartDate) return "—";
  let out = formatDateWithUserTimezone(visit.StartDate, {
    dateFormat: dateFormat || "MM/DD/YYYY",
    includeSeconds: false,
    excludeTime: true,
  });
  if (!out || out === "-" || out === "—") return out;
  out = out.replace(/\s+\d{1,2}:\d{2}(:\d{2})?(\s*[AaPp][Mm])?$/i, "").trim();
  return out || "—";
}

function visitSortTimestamp(v) {
  const d = moment(v?.StartDate);
  if (!d.isValid()) return 0;
  const base = d.clone().startOf("day").valueOf();
  const start = formatTimeForVisitDisplay(v?.StartTime);
  if (!start) return base;
  const m = moment(start, "hh:mm A", true);
  if (!m.isValid()) return base;
  return base + (m.hour() * 3600 + m.minute() * 60 + m.second()) * 1000;
}

function workerCustomerDisplayName(visit) {
  const c = visit?.CustomerData?.[0] ?? visit?.CustomerData;
  if (!c || typeof c !== "object") return "";
  const fn = c.FirstName || "";
  const ln = c.LastName || "";
  const parts = `${fn} ${ln}`.trim();
  if (parts) return parts;
  if (c.OwnerName) return String(c.OwnerName).trim();
  return "";
}

/** Admin/customer: joined names for sort/search (`workerDetails` or `AssignPerson`). */
function assignedWorkersDisplayFromVisit(visit) {
  const s = jobberVisitAssignedWorkersLabel(visit);
  return s || "—";
}

/** Stable seed for JobberAvatar (matches row workers when possible). */
function assignedWorkerAvatarSeed(visit) {
  const w = visit?.WorkerId;
  if (Array.isArray(w) && w.length > 0) return String(w[0]);
  if (w != null && w !== "") return String(w);
  return visit?.VisitId ?? "worker";
}

function resolveLatestWorkerStatusLabel(visit, workerIdStr) {
  const completed =
    visit?.IsComplete === true ||
    (workerIdStr &&
      Array.isArray(visit?.ConfirmComplete) &&
      visit.ConfirmComplete.map(String).includes(workerIdStr));
  if (completed) return "Completed";
  if (
    workerIdStr &&
    Array.isArray(visit?.ConfirmWorker) &&
    visit.ConfirmWorker.map(String).includes(workerIdStr)
  ) {
    return "Confirmed";
  }
  return String(visit?.Status ?? "").trim();
}

function resolveLatestCustomerStatusLabel(visit) {
  const st = String(visit?.Status ?? "").trim();
  const stLower = st.toLowerCase();
  if (visit?.IsComplete === true || stLower === "completed") return "Completed";
  if (
    visit?.IsConfirm === true ||
    visit?.IsConfirmByWorker === true ||
    (Array.isArray(visit?.ConfirmWorker) && visit.ConfirmWorker.length > 0) ||
    st === "Confirmed"
  ) {
    return "Confirmed";
  }
  return st;
}

function normalizeAdminScheduleRow(v) {
  return {
    ...v,
    ContractData: {
      ContractNumber: v.ContractNumber,
      Title: v.ItemName,
    },
    CustomerData: {
      FirstName: v.FirstName,
      LastName: v.LastName,
    },
    Location: {
      Address: v.Address,
      City: v.City,
      State: v.State,
    },
  };
}

function statusRank(s) {
  switch (String(s || "")) {
    case "Today":
      return 1;
    case "Upcoming":
      return 2;
    case "Scheduled":
      return 3;
    case "Late":
      return 4;
    case "Confirmed":
      return 5;
    case "Completed":
      return 6;
    default:
      return 50;
  }
}

function compareWorker(a, b, field, order, workerIdStr) {
  const mul = order === "asc" ? 1 : -1;
  switch (field) {
    case "CustomerName": {
      const va = workerCustomerDisplayName(a).toLowerCase();
      const vb = workerCustomerDisplayName(b).toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "WorkerName": {
      const va = assignedWorkersDisplayFromVisit(a).toLowerCase();
      const vb = assignedWorkersDisplayFromVisit(b).toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "StartDate": {
      return (visitSortTimestamp(a) - visitSortTimestamp(b)) * mul;
    }
    case "ContractNumber": {
      const va = parseInt(a?.ContractData?.ContractNumber, 10) || 0;
      const vb = parseInt(b?.ContractData?.ContractNumber, 10) || 0;
      return (va - vb) * mul;
    }
    case "ContractTitle": {
      const va = (a?.ContractData?.Title || a?.ItemName || "").toLowerCase();
      const vb = (b?.ContractData?.Title || b?.ItemName || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "VisitTitle": {
      const va = (a?.ItemName || "").toLowerCase();
      const vb = (b?.ItemName || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "Status": {
      const ra = statusRank(resolveLatestWorkerStatusLabel(a, workerIdStr));
      const rb = statusRank(resolveLatestWorkerStatusLabel(b, workerIdStr));
      if (ra !== rb) return (ra - rb) * mul;
      return (visitSortTimestamp(a) - visitSortTimestamp(b)) * mul;
    }
    default:
      return 0;
  }
}

function compareCustomer(a, b, field, order) {
  const mul = order === "asc" ? 1 : -1;
  switch (field) {
    case "WorkerName": {
      const va = customerVisitAssignedWorkerDisplay(a).toLowerCase();
      const vb = customerVisitAssignedWorkerDisplay(b).toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "StartDate": {
      return (visitSortTimestamp(a) - visitSortTimestamp(b)) * mul;
    }
    case "ContractNumber": {
      const va = parseInt(a?.ContractData?.ContractNumber, 10) || 0;
      const vb = parseInt(b?.ContractData?.ContractNumber, 10) || 0;
      return (va - vb) * mul;
    }
    case "ContractTitle": {
      const va = (a?.ContractData?.Title || a?.ItemName || "").toLowerCase();
      const vb = (b?.ContractData?.Title || b?.ItemName || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "VisitTitle": {
      const va = (a?.ItemName || "").toLowerCase();
      const vb = (b?.ItemName || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "Status": {
      const ra = statusRank(resolveLatestCustomerStatusLabel(a));
      const rb = statusRank(resolveLatestCustomerStatusLabel(b));
      if (ra !== rb) return (ra - rb) * mul;
      return (visitSortTimestamp(a) - visitSortTimestamp(b)) * mul;
    }
    default:
      return 0;
  }
}

function visitStatusBadge(label) {
  if (!label) return null;
  const text = String(label).trim();
  if (!text) return null;
  return (
    <JobberStatusBadge tone={getVisitAppointmentStatusTone(text)}>
      {text}
    </JobberStatusBadge>
  );
}

/** Admin schedule table — no Actions column (worker/customer keep Actions for mobile View) */
const HEADER_WORKER_ADMIN = [
  { label: "Customer Name", field: "CustomerName" },
  { label: "Contract", field: "ContractNumber" },
  { label: "Visit", field: "VisitTitle", wrap: true },
  { label: "Assigned Worker", field: "WorkerName" },
  { label: "Appointment Date", field: "StartDate" },
  { label: "Status", field: "Status" },
];

/** Staff worker — Actions exists for mobile collapse View only; hidden on desktop (see CSS) */
const HEADER_WORKER = [
  { label: "Customer Name", field: "CustomerName" },
  { label: "Contract #", field: "ContractNumber", wrap: true },
  { label: "Visit", field: "VisitTitle", wrap: true },
  { label: "Appointment Date", field: "StartDate" },
  { label: "Status", field: "Status" },
  { label: "Actions", field: "" },
];

const HEADER_CUSTOMER = [
  { label: "Assigned Worker", field: "WorkerName", wrap: true },
  { label: "Contract #", field: "ContractNumber", wrap: true },
  { label: "Visit", field: "VisitTitle", wrap: true },
  { label: "Appointment Date", field: "StartDate" },
  { label: "Status", field: "Status" },
  { label: "Actions", field: "" },
];

const CUSTOMER_STATUS_ITEMS = [
  { text: "All" },
  { text: "Today" },
  { text: "Upcoming" },
  { text: "Late" },
  { text: "Scheduled" },
  { text: "Confirmed" },
  { text: "Completed" },
];

const WORKER_STATUS_ITEMS = [
  { text: "All" },
  { text: "Today" },
  { text: "Upcoming" },
  { text: "Late" },
  { text: "Scheduled" },
  { text: "Confirmed" },
  { text: "Completed" },
];

function visitMatchesCustomerStatusToolbar(visit, filterLabel) {
  if (!filterLabel || filterLabel === "All") return true;
  return resolveLatestCustomerStatusLabel(visit) === filterLabel;
}

function visitMatchesWorkerStatusToolbar(visit, filterLabel, workerIdStr) {
  if (!filterLabel || filterLabel === "All") return true;
  return resolveLatestWorkerStatusLabel(visit, workerIdStr) === filterLabel;
}

function matchesSearchWorker(visit, rawQuery, workerIdStr) {
  const q = (rawQuery || "").trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    String(visit?.StartDate ?? ""),
    visit?.Status ?? "",
    resolveLatestWorkerStatusLabel(visit, workerIdStr),
    visit?.ItemName ?? "",
    visit?.ContractData?.Title ?? "",
    String(visit?.ContractData?.ContractNumber ?? ""),
    visit?.Location?.Address ?? "",
    visit?.Location?.City ?? "",
    visit?.Location?.State ?? "",
    workerCustomerDisplayName(visit),
  ]
    .join(" ")
    .toLowerCase();
  return q
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => haystack.includes(w));
}

/** Customer visits: `AssignPerson` or `workerDetails` (same shape as admin when present). */
function customerVisitAssignedWorkerDisplay(visit) {
  return jobberVisitAssignedWorkersLabel(visit);
}

function matchesSearchCustomer(visit, rawQuery) {
  const q = (rawQuery || "").trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    String(visit?.StartDate ?? ""),
    visit?.Status ?? "",
    resolveLatestCustomerStatusLabel(visit),
    visit?.ItemName ?? "",
    visit?.ContractData?.Title ?? "",
    String(visit?.ContractData?.ContractNumber ?? ""),
    visit?.Location?.Address ?? "",
    customerVisitAssignedWorkerDisplay(visit),
  ]
    .join(" ")
    .toLowerCase();
  return q
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => haystack.includes(w));
}

function matchesSearchAdmin(visit, rawQuery) {
  const q = (rawQuery || "").trim().toLowerCase();
  if (!q) return true;
  const name = [visit?.FirstName, visit?.LastName].filter(Boolean).join(" ").trim();
  const haystack = [
    String(visit?.StartDate ?? ""),
    visit?.Status ?? "",
    visit?.ItemName ?? "",
    String(visit?.ContractNumber ?? ""),
    name,
    assignedWorkersDisplayFromVisit(visit),
    visit?.Address ?? "",
    visit?.City ?? "",
    visit?.State ?? "",
  ]
    .join(" ")
    .toLowerCase();
  return q
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => haystack.includes(w));
}

/**
 * Today's appointments only (calendar day in user timezone), table view.
 * Route decides context: /todays-appointments (admin), /customers/todays-appointments, /staff-member/worker-todays-appointments.
 */
export default function TodaysAppointments() {
  const navigate = useNavigate();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [tokenDecode, setTokenDecode] = useState({});
  const [DateDecode, setDateDecode] = useState("MM/DD/YYYY");
  const formatDateWithUserTimezone = useUserTimezoneFormatter();

  const [search, setSearch] = useState("");
  const [visitStatusFilter, setVisitStatusFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState("StartDate");
  const [sortOrder, setSortOrder] = useState("asc");

  const variant = useMemo(() => {
    const p = location.pathname || "";
    if (p.includes("/staff-member/") && p.includes("worker-todays-appointments")) return "worker";
    if (p.includes("/customers/") && p.includes("todays-appointments")) return "customer";
    return "admin";
  }, [location.pathname]);

  const workerIdStr = getItem("worker_id") != null ? String(getItem("worker_id")) : "";
  const wid = workerIdStr;

  const fetchAuth = useCallback(async () => {
    try {
      const res = await handleAuth(navigate, location);
      setTokenDecode(res?.data);
      setDateDecode(res?.themes?.Format || "MM/DD/YYYY");
    } catch (e) {
      console.error(e);
      setDateDecode("MM/DD/YYYY");
    }
  }, [navigate, location]);

  useEffect(() => {
    fetchAuth();
  }, [fetchAuth]);

  const companyId = tokenDecode?.CompanyId;
  const userTz =
    tokenDecode?.Timezone && moment.tz.zone(tokenDecode.Timezone)
      ? tokenDecode.Timezone
      : "America/New_York";

  useEffect(() => {
    if (!companyId) return;

    const load = async () => {
      setLoader(true);
      try {
        if (variant === "admin") {
          const baseUrl = import.meta.env.VITE_BASE_API;
          const res = await AxiosInstance.get(`/v1/visit/visit-schedule`);
          if (res?.data?.statusCode === 200) {
            const raw = res?.data?.data || [];
            const normalized = raw.map(normalizeAdminScheduleRow);
            const { today } = categorizeVisitsByUserTimezone(normalized, userTz);
            setRows(today);
          } else {
            setRows([]);
          }
        } else if (variant === "customer") {
          const customerId = tokenDecode?.UserId;
          if (!customerId) {
            setRows([]);
            setLoader(false);
            return;
          }
          const res = await AxiosInstance.get(`/v1/visit/customer_visits/${companyId}/${customerId}`);
          if (res?.data?.statusCode === 200) {
            const data = res?.data?.data;
            let flat = [];
            if (data?.today || data?.upcoming || data?.past) {
              flat = [...(data.today || []), ...(data.upcoming || []), ...(data.past || [])];
            } else if (Array.isArray(data)) {
              flat = data;
            }
            const { today } = categorizeVisitsByUserTimezone(flat, userTz);
            setRows(today);
          } else {
            setRows([]);
          }
        } else {
          const WorkerId = getItem("worker_id");
          if (!WorkerId) {
            setRows([]);
            setLoader(false);
            return;
          }
          const baseUrl = import.meta.env.VITE_BASE_API;
          const res = await AxiosInstance.get(`/v1/worker/get-worker-visits/${companyId}/${WorkerId}`, {
            params: {
              pageSize: 1000,
              pageNumber: 0,
              search: "",
              statusFilter: "",
              sortField: "StartDate",
              sortOrder: "desc",
            },
          });
          if (res?.data?.statusCode === 200) {
            const data = res?.data?.data || [];
            const { today } = categorizeVisitsByUserTimezone(data, userTz);
            setRows(today);
          } else {
            setRows([]);
          }
        }
      } catch (e) {
        console.error(e);
        showToast.error("Failed to load today's appointments.");
        setRows([]);
      } finally {
        setLoader(false);
      }
    };

    load();
  }, [companyId, tokenDecode?.UserId, variant, userTz]);

  const statusFiltered = useMemo(() => {
    if (variant === "customer") {
      return rows.filter((v) => visitMatchesCustomerStatusToolbar(v, visitStatusFilter));
    }
    if (variant === "worker") {
      return rows.filter((v) => visitMatchesWorkerStatusToolbar(v, visitStatusFilter, wid));
    }
    return rows;
  }, [rows, visitStatusFilter, variant, wid]);

  const searched = useMemo(() => {
    if (variant === "admin") return statusFiltered.filter((v) => matchesSearchAdmin(v, search));
    if (variant === "customer") return statusFiltered.filter((v) => matchesSearchCustomer(v, search));
    return statusFiltered.filter((v) => matchesSearchWorker(v, search, wid));
  }, [statusFiltered, search, variant, wid]);

  const sorted = useMemo(() => {
    const list = [...searched];
    if (variant === "customer") {
      list.sort((a, b) => compareCustomer(a, b, sortField, sortOrder));
    } else {
      list.sort((a, b) => compareWorker(a, b, sortField, sortOrder, wid));
    }
    return list;
  }, [searched, sortField, sortOrder, variant, wid]);

  const countData = sorted.length;
  const pageSlice = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage]
  );

  useEffect(() => {
    setPage(0);
  }, [search, visitStatusFilter, variant]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [sorted.length, rowsPerPage, page]);

  const handleVisitStatusSelect = useCallback((text) => {
    setVisitStatusFilter(text || "All");
  }, []);

  const cu = tokenDecode?.CompanyUrl || CompanyUrl;
  const navigatePathWorker = cu ? `/${cu}/staff-member/appointment-confirm` : "/staff-member/appointment-confirm";
  const navigatePathCustomer = `/${CompanyUrl || "defaultcompany"}/customers/appointment-confirm`;
  const navigats = [...(location?.state?.navigats || []), "/appointment-confirm"];

  const handleViewAppointment = useCallback(
    (row) => {
      const navigatePath = variant === "customer" ? navigatePathCustomer : navigatePathWorker;
      const newPath = navigatePath.split("/");
      const navigationState = navigats;
      const baseState = {
        id: row.key,
        navigats: [
          ...(navigationState || []),
          `/${newPath[newPath.length - 1]}`,
        ],
      };
      const extra = row?.contractId != null ? { contractId: row.contractId } : {};
      navigate(navigatePath, { state: { ...baseState, ...extra } });
    },
    [variant, navigatePathCustomer, navigatePathWorker, navigats, navigate]
  );

  const goToContractDetail = useCallback(
    (e, contractId) => {
      if (e) e.stopPropagation();
      if (!contractId) return;
      const cu = tokenDecode?.CompanyUrl || CompanyUrl;
      if (!cu && variant !== "customer") return;

      if (variant === "customer") {
        const base = CompanyUrl || cu || "defaultcompany";
        const path = `/${base}/customers/contract-details`;
        const newPath = path.split("/");
        const navigatsContract = location?.state?.navigats
          ? [...location.state.navigats, "/contract-details"]
          : ["/contract-details"];
        navigate(path, {
          state: {
            id: contractId,
            navigats: [...navigatsContract, `/${newPath[newPath.length - 1]}`],
          },
        });
        return;
      }

      if (!cu) return;
      const navigatePath =
        tokenDecode?.Role === "Worker"
          ? `/${cu}/staff-member/worker-contract-details`
          : `/${cu}/contract-details`;
      const newPath = navigatePath.split("/");
      const baseNavigats = location?.pathname?.includes("staff-member")
        ? ["/index", "/staff-member", "/worker-contract"]
        : ["/index", "/contract"];
      navigate(navigatePath, {
        state: {
          id: contractId,
          navigats: [...baseNavigats, `/${newPath[newPath.length - 1]}`],
        },
      });
    },
    [CompanyUrl, location.pathname, location?.state?.navigats, navigate, tokenDecode?.CompanyUrl, tokenDecode?.Role, variant]
  );

  const cellDataWorkerAdmin = useMemo(
    () =>
      pageSlice.map((visit) => {
        const appointmentDateDisplay = formatAppointmentDateDisplay(
          visit,
          formatDateWithUserTimezone,
          DateDecode
        );
        const contractTitle = visit?.ContractData?.Title || visit?.ItemName || "—";
        const visitTitle = visit?.ItemName || "—";
        const num = visit?.ContractData?.ContractNumber;
        const customerName = workerCustomerDisplayName(visit);
        const displayName = customerName || "—";
        const contractLinkColor = isDonationMode ? "#29B3E2" : "#2563EB";
        const statusCell =
          variant === "worker"
            ? visitStatusBadge(resolveLatestWorkerStatusLabel(visit, wid))
            : visitStatusBadge(String(visit?.Status ?? "").trim());

        const rowRef = {
          key: visit?.VisitId,
          contractId: visit?.ContractId,
        };

        const viewCollapseAction =
          variant === "admin" ? null : (
            <Box
              key={`act-${visit?.VisitId}`}
              className="todays-appt-mobile-actions customer-list-actions-mobile"
              sx={{ display: "grid", width: "100%", maxWidth: "100%" }}
            >
              <MuiButton
                type="button"
                size="small"
                variant="contained"
                disableElevation
                fullWidth
                startIcon={
                  <VisibilityOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: isDonationMode ? "#0f766e" : COMPANY_VIEW_ICON_COLOR,
                    }}
                  />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewAppointment(rowRef);
                }}
                sx={{
                  ...todaysMobileActionBtnBase,
                  backgroundColor: isDonationMode
                    ? "rgba(104, 178, 203, 0.2)"
                    : COMPANY_VIEW_ACTION_BG,
                  color: isDonationMode ? "#0f766e" : COMPANY_VIEW_ICON_COLOR,
                  border: isDonationMode
                    ? "1px solid rgba(41, 179, 226, 0.3)"
                    : "1px solid rgba(30, 58, 138, 0.2)",
                  "&:hover": {
                    backgroundColor: isDonationMode
                      ? "rgba(104, 178, 203, 0.32)"
                      : "#c7d2fe",
                    borderColor: isDonationMode
                      ? "rgba(41, 179, 226, 0.45)"
                      : "rgba(30, 58, 138, 0.32)",
                  },
                }}
              >
                View
              </MuiButton>
            </Box>
          );

        const assignedWorkerCell = (
          <JobberAssignedWorkersCell
            key={`wk-${visit?.VisitId}`}
            rowKey={String(visit?.VisitId ?? "")}
            workerProfiles={jobberVisitWorkerProfiles(visit)}
            fallbackName={jobberVisitAssignedWorkersLabel(visit)}
            fallbackColorSeed={assignedWorkerAvatarSeed(visit)}
          />
        );

        return {
          key: visit?.VisitId,
          contractId: visit?.ContractId,
          value: [
            <JobberAvatar
              key={`av-${visit?.VisitId}`}
              name={displayName}
              colorSeed={visit?.VisitId}
            />,
            ...(variant === "admin"
              ? [
                  <JobberTableDocRefTitleCell
                    key={`doc-${visit?.VisitId}`}
                    refNumber={num}
                    onRefClick={(e) => goToContractDetail(e, visit?.ContractId)}
                    linkColor={contractLinkColor}
                    title={contractTitle}
                    titleLineBreakStyle="none"
                    wordsPerLine={0}
                    hugContent
                  />,
                ]
              : [
                  <JobberTableDocRefTitleCell
                    key={`doc-w-${visit?.VisitId}`}
                    refNumber={num}
                    onRefClick={(e) => goToContractDetail(e, visit?.ContractId)}
                    linkColor={contractLinkColor}
                    title={contractTitle}
                    titleLineBreakStyle="none"
                    wordsPerLine={0}
                  />,
                ]),
            <Typography
              key={`vt-${visit?.VisitId}`}
              sx={{
                fontSize: "0.9375rem",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                lineHeight: 1.5,
              }}
            >
              {visitTitle}
            </Typography>,
            ...(variant === "admin" ? [assignedWorkerCell] : []),
            <Typography key={`ap-${visit?.VisitId}`} sx={{ whiteSpace: "nowrap", fontSize: "0.9375rem" }}>
              {appointmentDateDisplay}
            </Typography>,
            statusCell,
            ...(variant === "admin" ? [] : [viewCollapseAction]),
          ],
        };
      }),
    [
      pageSlice,
      DateDecode,
      formatDateWithUserTimezone,
      variant,
      wid,
      isDonationMode,
      handleViewAppointment,
      goToContractDetail,
    ]
  );

  const cellDataCustomer = useMemo(
    () =>
      pageSlice.map((visit) => {
        const appointmentDateDisplay = formatAppointmentDateDisplay(
          visit,
          formatDateWithUserTimezone,
          DateDecode
        );
        const contractTitle = visit?.ContractData?.Title || visit?.ItemName || "—";
        const visitTitle = visit?.ItemName || "—";
        const num = visit?.ContractData?.ContractNumber;
        const profiles = jobberVisitWorkerProfiles(visit);
        const customerWorkerSeed =
          profiles.length === 1
            ? profiles[0]?.UserId ?? profiles[0]?.WorkerId ?? visit?.VisitId
            : visit?.AssignPerson?.UserId ?? visit?.VisitId;
        const contractLinkColor = isDonationMode ? "#29B3E2" : "#2563EB";
        const statusCell = visitStatusBadge(resolveLatestCustomerStatusLabel(visit));

        const rowRef = {
          key: visit?.VisitId,
          contractId: visit?.ContractId,
        };

        return {
          key: visit?.VisitId,
          contractId: visit?.ContractId,
          value: [
            <JobberAssignedWorkersCell
              key={`wk-${visit?.VisitId}`}
              rowKey={String(visit?.VisitId ?? "")}
              workerProfiles={profiles}
              fallbackName={jobberVisitAssignedWorkersLabel(visit)}
              fallbackColorSeed={customerWorkerSeed}
            />,
            <JobberTableDocRefTitleCell
              key={`doc-cust-${visit?.VisitId}`}
              refNumber={num}
              onRefClick={(e) => goToContractDetail(e, visit?.ContractId)}
              linkColor={contractLinkColor}
              title={contractTitle}
              titleLineBreakStyle="none"
              wordsPerLine={0}
            />,
            <Typography
              key={`vt-${visit?.VisitId}`}
              sx={{
                fontSize: "0.9375rem",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                lineHeight: 1.5,
              }}
            >
              {visitTitle}
            </Typography>,
            <Typography key={`ap-${visit?.VisitId}`} sx={{ whiteSpace: "nowrap", fontSize: "0.9375rem" }}>
              {appointmentDateDisplay}
            </Typography>,
            statusCell,
            <Box
              key={`act-${visit?.VisitId}`}
              className="todays-appt-mobile-actions customer-list-actions-mobile"
              sx={{ display: "grid", width: "100%", maxWidth: "100%" }}
            >
              <MuiButton
                type="button"
                size="small"
                variant="contained"
                disableElevation
                fullWidth
                startIcon={
                  <VisibilityOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: isDonationMode ? "#0f766e" : COMPANY_VIEW_ICON_COLOR,
                    }}
                  />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewAppointment(rowRef);
                }}
                sx={{
                  ...todaysMobileActionBtnBase,
                  backgroundColor: isDonationMode
                    ? "rgba(104, 178, 203, 0.2)"
                    : COMPANY_VIEW_ACTION_BG,
                  color: isDonationMode ? "#0f766e" : COMPANY_VIEW_ICON_COLOR,
                  border: isDonationMode
                    ? "1px solid rgba(41, 179, 226, 0.3)"
                    : "1px solid rgba(30, 58, 138, 0.2)",
                  "&:hover": {
                    backgroundColor: isDonationMode
                      ? "rgba(104, 178, 203, 0.32)"
                      : "#c7d2fe",
                    borderColor: isDonationMode
                      ? "rgba(41, 179, 226, 0.45)"
                      : "rgba(30, 58, 138, 0.32)",
                  },
                }}
              >
                View
              </MuiButton>
            </Box>,
          ],
        };
      }),
    [pageSlice, DateDecode, formatDateWithUserTimezone, isDonationMode, handleViewAppointment, goToContractDetail]
  );

  const headerData =
    variant === "customer"
      ? HEADER_CUSTOMER
      : variant === "admin"
        ? HEADER_WORKER_ADMIN
        : HEADER_WORKER;
  const cellData = variant === "customer" ? cellDataCustomer : cellDataWorkerAdmin;

  const jobberMobileCardProps = useMemo(() => {
    if (variant === "customer") {
      return {
        mobileCardPrimaryColumnIndex: 0,
        mobileCardSubtitleColumnIndex: 3,
        mobileCardStatusColumnIndex: 4,
        actionColumnIndex: 5,
      };
    }
    if (variant === "admin") {
      return {
        mobileCardPrimaryColumnIndex: 0,
        mobileCardSubtitleColumnIndex: 4,
        mobileCardStatusColumnIndex: 5,
        actionColumnIndex: null,
      };
    }
    return {
      mobileCardPrimaryColumnIndex: 0,
      mobileCardSubtitleColumnIndex: 3,
      mobileCardStatusColumnIndex: 4,
      actionColumnIndex: 5,
    };
  }, [variant]);

  const toolbar =
    variant === "admin" ? (
      <Box className="todays-appt-toolbar gap-2 d-flex align-items-center">
        <JobberSearch search={search} setSearch={setSearch} />
      </Box>
    ) : (
      <Box className="todays-appt-toolbar gap-2 d-flex align-items-center">
        <JobberSorting
          triggerVariant="light"
          dropdownItems={variant === "customer" ? CUSTOMER_STATUS_ITEMS : WORKER_STATUS_ITEMS}
          placeholder="Select Status"
          value={visitStatusFilter}
          onSelect={handleVisitStatusSelect}
        />
        <JobberSearch search={search} setSearch={setSearch} />
      </Box>
    );

  return (
    <Box
      className={[
        "appointments-jobber-table-wrap todays-appt-appointments-company-style",
        variant === "customer" || variant === "worker" ? "todays-appt-hide-actions-col-desktop" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}
    >
      <JobberListCard
        title="Today's Appointments"
        totalCount={countData}
        addButton={null}
        toolbar={toolbar}
        loader={loader}
        footer={
          <JobberPagination
            totalData={countData}
            currentData={rowsPerPage}
            dataPerPage={rowsPerPage}
            pageItems={[10, 25, 50]}
            page={page}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        }
      >
        <JobberTable
          headerData={headerData}
          cellData={cellData}
          {...jobberMobileCardProps}
          showMobileViewDetailsButton={false}
          setSortField={setSortField}
          setSortOrder={setSortOrder}
          sortOrder={sortOrder}
          sortField={sortField}
          isCollapse={false}
          isNavigate={variant !== "admin"}
          navigatePath={variant === "customer" ? navigatePathCustomer : navigatePathWorker}
          navigats={navigats}
          getNavigateState={(row) => (row?.contractId != null ? { contractId: row.contractId } : {})}
        />
      </JobberListCard>
    </Box>
  );
}
